import React, { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Routes, Route, useLocation } from "react-router-dom";
import InstagramFeed from "./InstagramFeed";
import ReviewPhotoWall from "./ReviewPhotoWall";
import CollectionPage from "./CollectionPage";
import ProductDetailPage from "./ProductDetailPage";
import ProductGrid from "./ProductGrid";
import ResinJourneyScroll from "./ResinJourneyScroll";
import { ALL_PRODUCTS, HOME_COLLECTION_PREVIEW_COUNT } from "./data/products";
import SiteLogo, { LOGO_ALT, LOGO_SRC, LOGO_WEBP } from "./components/SiteLogo";
import SiteNav, { scrollToSection } from "./components/SiteNav";

const PRODUCT_OPTIONS = [...ALL_PRODUCTS.map((p) => p.name), "Something else / Custom"];

const WHATSAPP_PHONE = "917984930698";
const WHATSAPP_DISPLAY = "+91 79849 30698";
const WHATSAPP_BASE_URL = `https://wa.me/${WHATSAPP_PHONE}`;
const WHATSAPP_GREETING = encodeURIComponent(
  "Hi Her Salty Studio! I'd like to enquire about a custom order."
);

function buildWhatsAppEnquiryUrl(form) {
  const text = [
    "Hi Her Salty Studio! I'd like to enquire about a custom order.",
    "",
    `Name: ${form.name.trim()}`,
    `Phone: ${form.phone.trim()}`,
    form.email.trim() ? `Email: ${form.email.trim()}` : null,
    `Product: ${form.product}`,
    form.message.trim() ? `Details: ${form.message.trim()}` : null,
  ]
    .filter(Boolean)
    .join("\n");
  return `${WHATSAPP_BASE_URL}?text=${encodeURIComponent(text)}`;
}

const BODY_HTML = `
<!-- SCROLL PROGRESS BAR -->
<div id="scrollBar"></div>


<!-- NAV + MOBILE MENU (SiteNav, rendered via portal) -->
<div id="site-nav-mount"></div>

<!-- HERO -->
<section class="hero hero--video-only" id="home">
  <div class="hero-video-bg">
    <picture>
      <source srcset="/home-hero-cover.webp" type="image/webp" />
      <img
        src="/home-hero-cover.jpeg"
        alt="Her Salty Studio — handcrafted resin art in a luxury interior"
        class="hero-cover-img"
        fetchpriority="high"
        decoding="async"
        width="1920"
        height="1080"
      />
    </picture>
  </div>
  <div class="hero-content hero-content--cover">
    <p class="hero-eyebrow">Slow-crafted · Made in India · One piece at a time</p>
    <h1>Where memories <em>set in gold</em></h1>
    <p class="hero-tagline">
      Engagement platters, preserved wedding blooms &amp; bespoke keepsakes — poured with patience,
      polished by hand, and made to live on your shelf forever.
    </p>
    <div class="hero-cta-group">
      <a href="/collection" class="btn-primary"><span>Explore the Collection</span></a>
      <a href="#custom" class="btn-enquire btn-enquire--hero">Commission Your Piece</a>
    </div>
    <div class="hero-badges">
      <span>100% Handmade</span>
      <span>Custom Orders Welcome</span>
      <span>Worldwide Shipping</span>
    </div>
  </div>
  <a href="#products" class="scroll-hint" aria-label="Scroll to collection">Discover</a>
</section>

<!-- PRODUCTS (same cards as /collection) -->
<section id="products">
  <div class="products-header reveal visible">
    <h2 class="section-title">Our Handcrafted <em>Collection</em></h2>
    <div class="section-divider"></div>
    <p class="products-preview-lede">A few favourite pieces from the studio. See every design on the full collection page.</p>
  </div>
  <div id="home-product-grid-mount"></div>
</section>

<!-- FLOATING WHATSAPP BUTTON -->
<a href="${WHATSAPP_BASE_URL}?text=${WHATSAPP_GREETING}" target="_blank" rel="noopener noreferrer" class="wa-float" title="Chat on WhatsApp — ${WHATSAPP_DISPLAY}" aria-label="Chat on WhatsApp — ${WHATSAPP_DISPLAY}">
  <svg width="26" height="26" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
</a>

<!-- STATS STRIP -->
<div class="stats-strip">
  <div class="stats-inner">
    <div class="stat-item reveal reveal-scale">
      <span class="stat-icon">🎁</span>
      <div class="stat-number"><span class="count-up" data-target="6000">0</span><span class="stat-accent">+</span></div>
      <div class="stat-label">Orders Delivered</div>
    </div>
    <div class="stat-item reveal reveal-scale reveal-delay-1">
      <span class="stat-icon">🌟</span>
      <div class="stat-number"><span class="count-up" data-target="3000">0</span><span class="stat-accent">+</span></div>
      <div class="stat-label">Happy Customers</div>
    </div>
    <div class="stat-item reveal reveal-scale reveal-delay-2">
      <span class="stat-icon">🎨</span>
      <div class="stat-number"><span class="count-up" data-target="50">0</span><span class="stat-accent">+</span></div>
      <div class="stat-label">Unique Designs</div>
    </div>
    <div class="stat-item reveal reveal-scale reveal-delay-3">
      <span class="stat-icon">📍</span>
      <div class="stat-number"><span class="count-up" data-target="25">0</span><span class="stat-accent">+</span></div>
      <div class="stat-label">Cities Reached</div>
    </div>
  </div>
</div>

<!-- ABOUT -->
<section id="about">
  <div class="about-inner">
    <div class="about-photo-wrap reveal reveal-left">
      <img class="about-story-photo" src="/IMG_9159.JPG" alt="The maker behind Her Salty Studio in the studio" width="1200" height="1800" loading="lazy" decoding="async" />
      
    </div>
    <div class="about-text reveal reveal-right reveal-delay-2">
      <div class="section-label">Our Story</div>
      <h2 class="section-title">Made with<br><em>intention</em></h2>
      <div class="section-divider"></div>
      <p>Hi, I'm Neha, the maker behind Her Salty Studio. <strong>Every piece you see here is poured, sanded, and finished by my own hands</strong> in my home studio. I believe in slow craft, meaningful keepsakes, and the quiet joy of owning something truly one-of-a-kind.</p>
      <p>From delicate ring platters for your most treasured moments to preserving your wedding flowers in crystal-clear resin — each creation carries a little piece of my heart into your home.</p>
      <div class="about-signature">— With love from my studio ✦</div>
    </div>
  </div>
</section>

<!-- HOW IT WORKS — scroll-scrubbed process video (ResinJourneyScroll) -->
<section id="process">
  <div id="process-journey-mount"></div>
</section>

<!-- REVIEWS (photo collage background + glowing title) -->
<section id="testimonials" class="testimonials-section">
  <div id="reviews-photo-wall-mount" class="reviews-photo-wall-root reveal"></div>
  <header class="testimonials-heading reveal">
    <div class="testimonials-heading__panel">
      <p class="testimonials-heading__kicker">Review wall</p>
      <h2 class="testimonials-heading__title">Moments from happy customers</h2>
    </div>
  </header>
</section>

<!-- INSTAGRAM -->
<section id="instagram">
  <div style="text-align:center; margin-bottom: 3rem;" class="reveal">
    <div class="section-label">@hersaltystudio</div>
    <h2 class="section-title">Follow our <em>studio life</em></h2>
    <div class="section-divider"></div>
  </div>
  <div id="insta-feed-mount"></div>
  <div style="text-align:center;">
    <a href="https://www.instagram.com/hersaltystudio/" target="_blank" rel="noopener noreferrer" class="insta-follow-btn" aria-label="Follow @hersaltystudio on Instagram">
      <span class="insta-follow-btn__glow" aria-hidden="true"></span>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="insta-follow-btn__icon" aria-hidden="true">
        <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"></path>
      </svg>
      <span class="insta-follow-btn__slot">
        <span class="insta-follow-btn__default">Follow on Instagram</span>
        <span class="insta-follow-btn__label">@hersaltystudio</span>
      </span>
    </a>
  </div>
</section>

<!-- CUSTOM CTA -->
<section id="custom">
  <div class="reveal">
    <div class="section-label" style="color: var(--gold-light);">Custom Orders</div>
    <h2 class="section-title">Have something <em>special</em><br>in mind?</h2>
    <p>We love creating custom pieces for weddings, birthdays, and every meaningful moment. Share your vision and we'll pour it into reality.</p>
    <a href="#" data-custom-order="true" class="btn-custom">Request a Custom Order</a>
  </div>
</section>

<!-- FOOTER -->
<footer>
  <div class="footer-inner">
    <div class="footer-brand">
      <a href="/" class="footer-logo-link" aria-label="${LOGO_ALT} — Home">
        <picture>
          <source srcset="${LOGO_WEBP}" type="image/webp" />
          <img src="${LOGO_SRC}" alt="${LOGO_ALT}" class="site-logo site-logo--footer" width="132" height="132" decoding="async" />
        </picture>
      </a>
      <div class="tagline">Crafted by hand. Kept forever.</div>
      <p>Every piece is made with intention, love, and slow craft in our home studio in India.</p>
      <div class="social-icons">
        <a href="https://www.instagram.com/hersaltystudio/" target="_blank" rel="noopener noreferrer" class="social-icon" title="Instagram" aria-label="Her Salty Studio on Instagram">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
        </a>
        <a href="${WHATSAPP_BASE_URL}?text=${WHATSAPP_GREETING}" target="_blank" rel="noopener noreferrer" class="social-icon" title="WhatsApp — ${WHATSAPP_DISPLAY}" aria-label="Chat on WhatsApp — ${WHATSAPP_DISPLAY}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
        </a>
      </div>
    </div>
    <div class="footer-col">
      <h4>Quick Links</h4>
      <ul>
        <li><a href="#home">Home</a></li>
        <li><a href="/collection">Collection</a></li>
        <li><a href="#about">About</a></li>
        <li><a href="#process">How It Works</a></li>
        <li><a href="#testimonials">Reviews</a></li>
      </ul>
    </div>
    <div class="footer-col">
      <h4>Contact</h4>
      <ul>
        <li><a href="https://www.instagram.com/hersaltystudio/" target="_blank" rel="noopener noreferrer">@hersaltystudio</a></li>
        <li><a href="${WHATSAPP_BASE_URL}?text=${WHATSAPP_GREETING}" target="_blank" rel="noopener noreferrer">${WHATSAPP_DISPLAY}</a></li>
        <li><a href="#custom">Custom Orders</a></li>
      </ul>
    </div>
  </div>
  <div class="footer-bottom">
    <span>©</span> 2024 Her Salty Studio · Made with <span>♥</span> in India
  </div>
</footer>
`;

/** Minimum digits for a phone number we can realistically reach on WhatsApp (10-digit Indian mobile, or country code + number). */
const MIN_PHONE_DIGITS = 10;

function CustomOrderModal({ onClose, initialProduct }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    product: PRODUCT_OPTIONS.includes(initialProduct) ? initialProduct : PRODUCT_OPTIONS[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [whatsAppUrl, setWhatsAppUrl] = useState("");
  const modalRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    modalRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      // Keep Tab inside the dialog.
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === modalRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [onClose]);

  const updateField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Please share your name and phone number so we can reach you.");
      return;
    }
    if (form.phone.replace(/\D/g, "").length < MIN_PHONE_DIGITS) {
      setError("Please enter a valid WhatsApp number (at least 10 digits, with country code if outside India).");
      return;
    }
    setSubmitting(true);
    const url = buildWhatsAppEnquiryUrl(form);
    setWhatsAppUrl(url);
    window.open(url, "_blank", "noopener,noreferrer");
    setDone(true);
    setSubmitting(false);
  };

  return (
    <div className="com-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="com-modal" role="dialog" aria-modal="true" aria-labelledby="com-title" ref={modalRef} tabIndex={-1}>
        <button className="com-close" onClick={onClose} aria-label="Close">✕</button>
        {done ? (
          <div className="com-success">
            <div className="com-success-icon">✦</div>
            <div className="com-success-title" id="com-title">Thank <em>you</em>!</div>
            <p>
              WhatsApp should open with your enquiry details — if it didn't, tap the button below.
              Send the message and our studio will reach out within 24 hours.
            </p>
            <a className="com-submit com-submit--link" href={whatsAppUrl} target="_blank" rel="noopener noreferrer">
              Open WhatsApp
            </a>
            <button className="com-submit com-submit--secondary" type="button" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className="com-header">
              <SiteLogo variant="modal" className="com-logo" />
              <div className="com-eyebrow">Custom Order</div>
              <div className="com-title" id="com-title">
                Let's craft something <em>special</em>
              </div>
              <div className="com-subtitle">Share your vision — we'll pour it into reality.</div>
            </div>
            <form className="com-form" onSubmit={submit}>
              <div className="com-row">
                <div className="com-field">
                  <label className="com-label" htmlFor="com-name">Your Name</label>
                  <input
                    id="com-name"
                    className="com-input"
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={updateField("name")}
                    placeholder="Priya Sharma"
                    required
                  />
                </div>
                <div className="com-field">
                  <label className="com-label" htmlFor="com-phone">WhatsApp Number</label>
                  <input
                    id="com-phone"
                    className="com-input"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={updateField("phone")}
                    placeholder="+91 98xxx xxxxx"
                    aria-describedby="com-phone-hint"
                    required
                  />
                  <p className="com-hint" id="com-phone-hint">We ship worldwide — add your country code if you're outside India.</p>
                </div>
              </div>
              <div className="com-field">
                <label className="com-label" htmlFor="com-email">Email (optional)</label>
                <input
                  id="com-email"
                  className="com-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={updateField("email")}
                  placeholder="you@example.com"
                />
              </div>
              <div className="com-field">
                <label className="com-label" htmlFor="com-product">Product of Interest</label>
                <select
                  id="com-product"
                  className="com-select"
                  value={form.product}
                  onChange={updateField("product")}
                >
                  {PRODUCT_OPTIONS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="com-field">
                <label className="com-label" htmlFor="com-message">Tell us about your idea</label>
                <textarea
                  id="com-message"
                  className="com-textarea"
                  value={form.message}
                  onChange={updateField("message")}
                  placeholder="Colors, size, occasion, special memories you'd like preserved..."
                />
              </div>
              {error && <div className="com-error">{error}</div>}
              <button className="com-submit" type="submit" disabled={submitting}>
                {submitting ? "Opening WhatsApp..." : "Continue on WhatsApp"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function HomePage() {
  const initRef = useRef(false);
  const pageShellRef = useRef(null);
  const { hash } = useLocation();
  const [mounts, setMounts] = useState({
    nav: null,
    products: null,
    insta: null,
    reviews: null,
    process: null,
  });

  useEffect(() => {
    const shell = pageShellRef.current;
    if (!shell) return;
    if (shell.dataset.htmlLoaded !== "true") {
      shell.innerHTML = BODY_HTML;
      shell.dataset.htmlLoaded = "true";
    }
    setMounts({
      nav: document.getElementById("site-nav-mount"),
      products: document.getElementById("home-product-grid-mount"),
      insta: document.getElementById("insta-feed-mount"),
      reviews: document.getElementById("reviews-photo-wall-mount"),
      process: document.getElementById("process-journey-mount"),
    });
  }, []);

  // The page body is injected after load, so the browser's own hash scroll finds nothing.
  // Scroll ourselves (direct loads of /#custom, nav links from other pages) — but only after the
  // portalled sections above the target (product grid) have mounted, or the offset is stale.
  const portalsMounted = Boolean(mounts.products);
  useEffect(() => {
    const id = hash.replace(/^#/, "");
    if (!id || id === "home" || !portalsMounted) return undefined;
    const raf = requestAnimationFrame(() => scrollToSection(id, "auto"));
    return () => cancelAnimationFrame(raf);
  }, [hash, portalsMounted]);

  useEffect(() => {
    if (initRef.current) return undefined;
    initRef.current = true;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const scrollBar = document.getElementById("scrollBar");
    let scrollRaf = 0;
    const onScroll = () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        const y = window.scrollY;
        // Looked up per frame: the nav is portalled in after this effect runs.
        const navbar = document.getElementById("navbar");
        if (navbar) navbar.classList.toggle("scrolled", y > 50);
        if (scrollBar) {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          scrollBar.style.width = max > 0 ? `${(y / max) * 100}%` : "0%";
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    const revealSelector = ".reveal, .reveal-left, .reveal-right, .reveal-scale";
    let revealObserver;
    if (reducedMotion) {
      document.querySelectorAll(`${revealSelector}, .section-label, .stat-item, .reviews-photo-wall-root`).forEach((el) => {
        el.classList.add("visible");
      });
    } else {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add("visible");
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
      );
      document.querySelectorAll(revealSelector).forEach((el) => revealObserver.observe(el));

      const labelObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => e.target.classList.toggle("visible", e.isIntersecting));
        },
        { threshold: 0.5 },
      );
      document.querySelectorAll(".section-label").forEach((el) => labelObs.observe(el));

      const statItemObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) e.target.classList.add("visible");
          });
        },
        { threshold: 0.4 },
      );
      document.querySelectorAll(".stat-item").forEach((el) => statItemObs.observe(el));
    }

    const formatCount = (n) => (n >= 1000 ? n.toLocaleString("en-IN") : String(n));
    function animateCount(el) {
      const target = parseInt(el.dataset.target, 10);
      if (reducedMotion) {
        el.textContent = formatCount(target);
        return;
      }
      const duration = 1800;
      const startTime = performance.now();
      function update(now) {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 1 - (1 - progress) ** 3;
        const current = Math.floor(ease * target);
        el.textContent = formatCount(current);
        if (progress < 1) requestAnimationFrame(update);
        else el.textContent = formatCount(target);
      }
      requestAnimationFrame(update);
    }

    const countObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !entry.target.dataset.animated) {
            entry.target.dataset.animated = "true";
            animateCount(entry.target);
          }
        });
      },
      { threshold: 0.5 },
    );
    document.querySelectorAll(".count-up").forEach((el) => countObserver.observe(el));

    const anchorHandler = (anchor) => (e) => {
      const href = anchor.getAttribute("href");
      if (!href || href === "#" || anchor.dataset.customOrder) return;
      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
      }
    };
    const anchors = document.querySelectorAll('a[href^="#"]');
    const anchorListeners = [];
    anchors.forEach((a) => {
      const fn = anchorHandler(a);
      a.addEventListener("click", fn);
      anchorListeners.push([a, fn]);
    });

    return () => {
      initRef.current = false;
      window.removeEventListener("scroll", onScroll);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      revealObserver?.disconnect();
      countObserver.disconnect();
      anchorListeners.forEach(([a, fn]) => a.removeEventListener("click", fn));
    };
  }, []);

  return (
    <>
      <div ref={pageShellRef} />
      {mounts.nav && createPortal(<SiteNav variant="home" />, mounts.nav)}
      {mounts.products &&
        createPortal(
          <ProductGrid
            products={ALL_PRODUCTS.slice(0, HOME_COLLECTION_PREVIEW_COUNT)}
            showViewAll
            startVisible
          />,
          mounts.products,
        )}
      {mounts.insta && createPortal(<InstagramFeed />, mounts.insta)}
      {mounts.reviews && createPortal(<ReviewPhotoWall />, mounts.reviews)}
      {mounts.process && createPortal(<ResinJourneyScroll />, mounts.process)}
    </>
  );
}

function App() {
  // null = closed; otherwise { product } preselects the piece the user was looking at.
  const [modal, setModal] = useState(null);
  const closeModal = useCallback(() => setModal(null), []);

  useEffect(() => {
    const clickHandler = (e) => {
      const t = e.target.closest("[data-custom-order]");
      if (t) {
        e.preventDefault();
        setModal({ product: t.dataset.product || "" });
      }
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, []);

  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/collection" element={<CollectionPage />} />
        <Route path="/collection/:slug" element={<ProductDetailPage />} />
      </Routes>
      {modal && <CustomOrderModal onClose={closeModal} initialProduct={modal.product} />}
    </>
  );
}

export default App;
